// websiteSuggestion.service.ts
// Website Suggestion (public endpoints) service.
// Important unit please don't modify without coordinating with the teammate.
// @athour huang

import unauthenticatedApi from "@/utils/unauthenticatedAxios";
import api from "@/utils/axios";
import { getAccessToken } from "@/utils/accessTokenStore";

const CHECK_PATH = "/public/v2/website-suggestions/check";
const CREATE_PATH = "/public/v2/website-suggestions";
const GUESS_REGION_PATH = "/public/v2/website-suggestions/guess-region";

const ML_CATEGORY_SUGGEST_PATH = "/public/ml/category-suggest";

type SuggestionCheckResponse = {
  exists: boolean;
  message?: string;
};

export type CheckBothResult =
  | { ok: true; exists: boolean }
  | { ok: false; exists: false };

export type CreateWebsiteSuggestionRequest = {
  title: string;
  description: string;
  urlLandingPage: string;
  address: string | null;
  zipCode: string | null;

  // Leaf only
  category1Id: string | null;

  // - null means leaf-only mode (backend should compute depth from the category tree)
  // - if you later want to persist a full breadcrumb path, send the full path here (root -> leaf)
  categoryPathIds: string[] | null;

  regionId: string | null;

  // - null means leaf-only mode
  // - if you later want to persist a full breadcrumb path, send the full path here (root -> leaf)
  regionPathIds: string[] | null;
};

const buildWwwVariant = (canonicalUrl: string) => {
  const host = new URL(canonicalUrl).hostname;
  return `https://www.${host}`;
};

const checkOne = async (
  url: string
): Promise<{ ok: true; exists: boolean } | { ok: false }> => {
  try {
    // Backend endpoint expects POST; no request body needed.
    const res = await unauthenticatedApi.post<SuggestionCheckResponse>(
      `${CHECK_PATH}?url=${encodeURIComponent(url)}`,
      null,
      { headers: { Accept: "application/json" } }
    );

    return { ok: true, exists: Boolean(res.data?.exists) };
  } catch {
    return { ok: false };
  }
};

export const checkSuggestionUrlBoth = async (
  canonicalUrl: string
): Promise<CheckBothResult> => {
  const wwwUrl = buildWwwVariant(canonicalUrl);

  const [c1, c2] = await Promise.all([checkOne(canonicalUrl), checkOne(wwwUrl)]);

  if (!c1.ok || !c2.ok) return { ok: false, exists: false };
  return { ok: true, exists: c1.exists || c2.exists };
};

/**
 * - category1Id is a leaf UUID (string) only.
 * - In leaf-only mode, send categoryPathIds/regionPathIds as null.
 * - Do NOT auto-wrap leaf ids into [leaf] here, otherwise backend will treat it as a path
 *   and depth might become 0 for the single element.
 */
export const createWebsiteSuggestion = async (
  payload: CreateWebsiteSuggestionRequest
) => {
  const hasToken =
    typeof window !== "undefined" && Boolean(getAccessToken());

  const client = hasToken ? api : unauthenticatedApi;

  const safePayload: CreateWebsiteSuggestionRequest = {
    title: payload.title,
    description: payload.description,
    urlLandingPage: payload.urlLandingPage,
    address: payload.address ?? null,
    zipCode: payload.zipCode ?? null,

    category1Id: payload.category1Id ?? null,

    // Keep as-is; caller decides null (leaf-only) or full path.
    categoryPathIds: payload.categoryPathIds ?? null,

    regionId: payload.regionId ?? null,

    // Keep as-is; caller decides null (leaf-only) or full path.
    regionPathIds: payload.regionPathIds ?? null,
  };

  try {
    const res = await client.post(CREATE_PATH, safePayload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (err: any) {
    // Re-throw enriched error so UI can debug exact backend response.
    const status = err?.response?.status;
    const data = err?.response?.data;

    const enriched = new Error(
      `createWebsiteSuggestion failed (status=${status ?? "unknown"})`
    ) as any;

    enriched.response = { status, data };
    enriched.original = err;

    throw enriched;
  }
};

export type GuessRegionResult =
  | { ok: true; regionId: string; regionName: string | null }
  | { ok: true; regionId: null; regionName: null }
  | { ok: false; regionId: null; regionName: null };

export const guessRegionByZipCode = async (
  zipCode: string
): Promise<GuessRegionResult> => {
  try {
    // Treat 204 (No Content) as a valid "no match" response.
    const res = await unauthenticatedApi.get(GUESS_REGION_PATH, {
      params: { zipCode },
      validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
    });

    if (res.status === 204) {
      return { ok: true, regionId: null, regionName: null };
    }

    const regionIdRaw = res.data?.regionId;
    const regionId =
      typeof regionIdRaw === "string" && regionIdRaw.trim()
        ? regionIdRaw.trim()
        : null;

    if (!regionId) {
      return { ok: true, regionId: null, regionName: null };
    }

    const regionNameRaw = res.data?.regionName;
    const regionName =
      typeof regionNameRaw === "string" && regionNameRaw.trim()
        ? regionNameRaw.trim()
        : null;

    return { ok: true, regionId, regionName };
  } catch {
    return { ok: false, regionId: null, regionName: null };
  }
};

// AI category suggest

export type AiCategorySuggestRequest = {
  title: string;
  description: string;
  lang: "en" | "de";
};

export type AiCategorySuggestResponse = {
  // Leaf
  categoryId: string;
  categoryName: string;
  depth: number;

  // UI display only
  categoryPathIds: string[];
  categoryPathNames: string[];
};

export type AiSuggestResult =
  | { ok: true; data: AiCategorySuggestResponse }
  | {
      ok: false;
      kind:
        | "timeout"
        | "bad_gateway"
        | "server"
        | "client"
        | "network"
        | "unknown";
      status?: number;
      rawMessage?: string;
    };

const extractBackendMessage = (data: any): string | undefined => {
  if (!data) return undefined;
  if (typeof data === "string") return data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  return undefined;
};

// Normalize backend AI response keys (some backends return `name` instead of `categoryName`, etc.)
const normalizeAiResponse = (raw: any): AiCategorySuggestResponse => {
  const categoryId = String(raw?.categoryId ?? raw?.id ?? "");
  const categoryName = String(raw?.categoryName ?? raw?.name ?? "");
  const depth = Number(raw?.depth ?? 0);

  const categoryPathIds = Array.isArray(raw?.categoryPathIds)
    ? raw.categoryPathIds.map((x: any) => String(x))
    : Array.isArray(raw?.pathIds)
    ? raw.pathIds.map((x: any) => String(x))
    : [];

  const categoryPathNames = Array.isArray(raw?.categoryPathNames)
    ? raw.categoryPathNames.map((x: any) => String(x))
    : Array.isArray(raw?.pathNames)
    ? raw.pathNames.map((x: any) => String(x))
    : [];

  return { categoryId, categoryName, depth, categoryPathIds, categoryPathNames };
};

export const suggestCategoryByAi = async (
  payload: AiCategorySuggestRequest
): Promise<AiSuggestResult> => {
  try {
    const res = await unauthenticatedApi.post(
      ML_CATEGORY_SUGGEST_PATH,
      payload,
      {
        headers: { "Content-Type": "application/json" },
        timeout: 60_000,
        validateStatus: (s) => s >= 200 && s < 300,
      }
    );

    return { ok: true, data: normalizeAiResponse(res.data) };
  } catch (err: any) {
    const code = err?.code as string | undefined;

    if (code === "ECONNABORTED") {
      return { ok: false, kind: "timeout" };
    }

    const status = err?.response?.status as number | undefined;
    const rawMessage =
      extractBackendMessage(err?.response?.data) ?? err?.message;

    if (status) {
      if (status === 502 || status === 503 || status === 504) {
        return { ok: false, kind: "bad_gateway", status, rawMessage };
      }
      if (status >= 500) {
        return { ok: false, kind: "server", status, rawMessage };
      }
      if (status >= 400) {
        return { ok: false, kind: "client", status, rawMessage };
      }
    }

    if (rawMessage) {
      return { ok: false, kind: "network", rawMessage };
    }

    return { ok: false, kind: "unknown" };
  }
};
